import React, { ComponentType } from 'react';
import * as Sentry from '@sentry/react';

/**
 * Higher-order component that wraps a component with Sentry performance monitoring
 * 
 * @param Component The component to wrap
 * @param options Additional options for the performance tracking
 * @returns A wrapped component with performance tracking
 */
export function withPerformanceTracking<P extends object>(
  Component: ComponentType<P>,
  options?: {
    /**
     * The name to use for the transaction. Defaults to Component.displayName || Component.name
     */
    componentName?: string;
    /**
     * Whether to include component props in the transaction data
     */
    includeProps?: boolean;
    /**
     * Additional data to include in the transaction
     */
    additionalData?: Record<string, any>;
  }
) {
  const componentName = options?.componentName || Component.displayName || Component.name || 'UnknownComponent';
  
  // Create the wrapped component
  const WrappedComponent = (props: P) => {
    return (
      <Sentry.Profiler
        name={`Component:${componentName}`}
        includeRender={true}
        {...(options?.includeProps ? { metadata: { props } } : {})}
        {...(options?.additionalData ? { metadata: { ...options.additionalData } } : {})}
      >
        <Component {...props} />
      </Sentry.Profiler>
    );
  };
  
  // Set the display name for easier debugging
  WrappedComponent.displayName = `WithPerformanceTracking(${componentName})`;
  
  return WrappedComponent;
}

/**
 * Creates a Sentry transaction for measuring performance of a specific operation
 * 
 * @param name The name of the transaction
 * @param operation The operation to perform (can be asynchronous)
 * @param metadata Additional data to include with the transaction
 * @returns The result of the operation
 */
export async function trackPerformance<T>(
  name: string,
  operation: () => Promise<T> | T,
  metadata?: Record<string, any>
): Promise<T> {
  const transaction = Sentry.startTransaction({
    name,
    metadata
  });
  
  try {
    Sentry.configureScope(scope => {
      scope.setSpan(transaction);
    });
    
    const result = await operation();
    transaction.finish();
    return result;
  } catch (error) {
    transaction.setStatus('internal_error');
    transaction.finish();
    throw error;
  }
}

/**
 * Wraps a React component with Sentry performance monitoring
 * Usage: export default sentryTrace(MyComponent, { componentName: "CustomName" });
 */
export const sentryTrace = Sentry.withProfiler;
